import { Box } from "@mui/material";
import { Navigate, useSearchParams } from "react-router-dom";
import BouncesPanel from "@/components/reports/BouncesPanel";
import RedemptionsPanel from "@/components/reports/RedemptionsPanel";
import TopGiftsPanel from "@/components/reports/TopGiftsPanel";
import { REDEMPTIONS_TEXT, REPORTS_TAB_PARAM, REPORTS_TABS } from "@/constants/reportsRedemptions";
import { usePermissions } from "@/hooks/usePermissions";

type ReportTabId = (typeof REPORTS_TABS)[number]["id"];

const TAB_IDS = REPORTS_TABS.map((t) => t.id);

function isReportTabId(value: string | null): value is ReportTabId {
  return !!value && (TAB_IDS as string[]).includes(value);
}

function TabButton({
  active,
  enabled,
  label,
  onClick,
}: {
  active: boolean;
  enabled: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-disabled={enabled ? undefined : "true"}
      tabIndex={enabled ? 0 : -1}
      title={enabled ? undefined : REDEMPTIONS_TEXT.comingSoon}
      onClick={enabled ? onClick : undefined}
      style={{
        appearance: "none",
        background: "none",
        border: "none",
        cursor: enabled ? "pointer" : "not-allowed",
        opacity: enabled ? 1 : 0.4,
        padding: "10px 4px",
        marginBottom: -1,
        fontFamily: "var(--font-lato)",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "0.2px",
        color: active ? "var(--color-rodeo-black)" : "var(--color-graphite)",
        borderBottom: `2px solid ${active ? "var(--color-dark-dust)" : "transparent"}`,
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );
}

/** The Reports screen's tab bar — Redemptions, Bounces, and Top Gifts. The page `<h1>` comes from the Drawer topbar, not here. */
export default function Reports() {
  const { isGfg, isLoaded } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get(REPORTS_TAB_PARAM);
  const tab: ReportTabId = isReportTabId(rawTab) ? rawTab : "redemptions";

  function handleTabClick(id: ReportTabId) {
    const next = new URLSearchParams(searchParams);
    next.set(REPORTS_TAB_PARAM, id);
    setSearchParams(next, { replace: true });
  }

  if (!isLoaded) return null;
  if (isGfg) return <Navigate to="/gfg-dashboard" replace />;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: "var(--font-lato)" }}>
      <div
        role="tablist"
        style={{
          display: "flex",
          gap: 28,
          borderBottom: "1px solid var(--color-card-border)",
          marginBottom: 24,
        }}
      >
        {REPORTS_TABS.map((t) => (
          <TabButton
            key={t.id}
            active={tab === t.id}
            enabled={t.enabled}
            label={t.label}
            onClick={() => handleTabClick(t.id)}
          />
        ))}
      </div>

      {tab === "redemptions" && <RedemptionsPanel />}
      {tab === "bounces" && <BouncesPanel />}
      {tab === "topgifts" && <TopGiftsPanel />}
    </Box>
  );
}
